import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom, from, Observable, of, timer } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';

import { RuntimeConfigService } from './runtime-config.service';
import { GlobalPollingService } from './global-polling.service';
import { UserService } from './user-service.service';
import { User } from '../app/models/user.model';
import { SystemNoticeTypeEnum } from '../app/models/system-notice-type.enum';
import { UserTypeEnum } from '../app/models/user-type.enum';
import { PaginationModel } from '../app/apps/shared/pagination-model';
import { AdaptiveContainerService } from '../app/apps/shared/adaptive-container/adaptive-container.service';
import { SystemNoticeSnackbarComponent } from '../app/apps/shared/system-notice-snackbar/system-notice-snackbar.component';
import { SystemNoticePopupComponent, SystemNoticePopupDialogData } from '../app/apps/shared/modal/system-notice-popup/system-notice-popup.component';
import {
  SystemNoticeMoreInfoComponent, SystemNoticeMoreInfoDialogData, SystemNoticeMoreInfoDialogResult
} from '../app/apps/shared/modal/system-notice-more-info/system-notice-more-info.component';

/** Marks a notice built from the admin form; it never reaches the server. */
export const PREVIEW_NOTICE_ID = -1;

/**
 * Shows the active app-wide notice (e.g. a maintenance window) as a toast or a popup
 * depending on its NoticeType. Checks at login and then at most every CHECK_INTERVAL while
 * the app is in the foreground, so a notice published mid-session still reaches users.
 */
@Injectable({
  providedIn: 'root'
})
export class SystemNoticeService {
  private apiUrl: string;

  /** How often to ask the server for a notice; every trigger source coalesces to this. */
  private readonly CHECK_INTERVAL = 5 * 60 * 1000; // 5 min
  /** Heartbeat that re-evaluates the throttle; a cheap no-op between real checks. */
  private readonly HEARTBEAT = 60 * 1000;

  private lastCheckedAt = 0;
  private checkInFlight = false;
  /** A notice is currently on screen (toast or popup), so don't fetch another under it. */
  private noticeShowing = false;

  /** A dismissal in flight, so a double-tap can't fire a second POST. */
  private dismissing = new Set<number>();

  constructor(
    private http: HttpClient,
    private runtimeConfig: RuntimeConfigService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private adaptiveContainer: AdaptiveContainerService,
    private userService: UserService,
    private globalPolling: GlobalPollingService
  ) {
    this.apiUrl = this.runtimeConfig.currentConfig.apiUrl + '/mainmenu';

    // Subscribe to config changes to update apiUrl
    this.runtimeConfig.config$.subscribe(config => {
      this.apiUrl = config.apiUrl + '/mainmenu';
    });

    // Check as soon as auth has produced a profile, then keep checking while the app is in
    // use: the heartbeat covers time spent in the app, and the pause-state resume covers
    // returning to the foreground (GlobalPollingService flips it on web tab focus and on
    // Capacitor app resume). check() throttles, so extra triggers cost nothing.
    this.userService.currentUserProfile$
      .pipe(filter(profile => !!profile))
      .subscribe(() => this.check());

    timer(this.HEARTBEAT, this.HEARTBEAT).subscribe(() => this.check());

    this.globalPolling.getPauseState()
      .pipe(filter(paused => !paused))
      .subscribe(() => this.check());
  }

  private async check(): Promise<void> {
    // Skip while a notice is on screen: the server would return that same undismissed
    // notice, and a dismissal chains to the next one anyway.
    if (this.checkInFlight || this.noticeShowing) { return; }
    if (Date.now() - this.lastCheckedAt < this.CHECK_INTERVAL) { return; }

    this.checkInFlight = true;
    try {
      // Not authenticated: leave lastCheckedAt alone so the first post-login check is
      // immediate. A failed fetch retries on the heartbeat once the throttle lapses.
      if (!await this.userService.isUserAuthenticated()) { return; }
      this.lastCheckedAt = Date.now();

      // The server returns at most one notice - a snack bar shows a single message at a
      // time, so opening a second would silently replace the first.
      const notice = await firstValueFrom(
        this.http.get<SystemNotice | null>(`${this.apiUrl}/GetActiveSystemNoticeAsync/`)
          .pipe(catchError(() => of(null)))
      );

      // The dismissing guard also covers a fetch racing a dismissal POST, which could
      // otherwise re-show the notice that's being dismissed.
      if (notice && !this.dismissing.has(notice.Id)) { this.show(notice); }
    }
    finally {
      this.checkInFlight = false;
    }
  }

  private show(notice: SystemNotice): void {
    this.noticeShowing = true;

    if (notice.NoticeType === SystemNoticeTypeEnum.Popup) {
      this.showPopup(notice);
    }
    else {
      this.showToast(notice);
    }
  }

  private showToast(notice: SystemNotice): void {
    // openFromComponent rather than open() - the text API can't render the alert and X icons.
    // No duration: a maintenance notice stays until the user acts on it, because only an
    // explicit action counts as a dismissal.
    const snackBarRef = this.snackBar.openFromComponent(SystemNoticeSnackbarComponent, {
      data: {
        notice,
        onMoreInfo: () => this.showMoreInfo(notice, () => snackBarRef.dismiss())
      },
      panelClass: 'system-notice-snackbar',
      verticalPosition: 'bottom',
      horizontalPosition: 'center'
    });

    snackBarRef.afterDismissed().subscribe(() => {
      if (notice.Id !== PREVIEW_NOTICE_ID) { this.noticeShowing = false; }
    });
    snackBarRef.onAction().subscribe(() => this.dismiss(notice.Id));
  }

  private showPopup(notice: SystemNotice): void {
    const data: SystemNoticePopupDialogData = {
      notice,
      onMoreInfo: () => this.showMoreInfo(notice, () => ref.close())
    };

    const ref = this.adaptiveContainer.open<SystemNoticePopupComponent, SystemNoticePopupDialogData>(
      SystemNoticePopupComponent,
      { data, width: '560px', autoFocus: false, disableClose: true }
    ).dialogRef;

    // Dismissing takes a deliberate click on the X or the Dismiss button (disableClose
    // blocks the backdrop and Escape); record it on whichever closed the dialog.
    ref.afterClosed().subscribe(() => {
      if (notice.Id !== PREVIEW_NOTICE_ID) { this.noticeShowing = false; }
      this.dismiss(notice.Id);
    });
  }

  /**
   * The More Info side panel, shared by both presentations. Feedback can only reach the server
   * as part of a dismissal, so submitting it also closes the notice behind the panel.
   */
  private showMoreInfo(notice: SystemNotice, closeNotice: () => void): void {
    const data: SystemNoticeMoreInfoDialogData = {
      title: notice.MoreInfoTitle,
      html: notice.MoreInfoDescription,
      files: notice.Files ?? [],
      allowFeedback: !!notice.AllowFeedback
    };

    this.dialog.open<SystemNoticeMoreInfoComponent, SystemNoticeMoreInfoDialogData, SystemNoticeMoreInfoDialogResult>(
      SystemNoticeMoreInfoComponent,
      {
        data,
        position: { right: '0', top: '0' },
        height: '100vh',
        width: window.innerWidth <= 768 ? '100vw' : '420px',
        maxWidth: '100vw',
        panelClass: 'system-notice-side-panel'
      }
    ).afterClosed().subscribe(result => {
      if (!result?.dismiss) { return; }

      // Closing the popup fires its own afterClosed(), which dismisses without feedback -
      // so send this one first and let the dismissing guard swallow the second call.
      this.dismiss(notice.Id, result.feedback);
      closeNotice();
    });
  }

  /** The single dismissal path. Returns the next notice to show, if there is one. */
  private dismiss(noticeId: number, feedback?: string | null): void {
    if (noticeId === PREVIEW_NOTICE_ID || this.dismissing.has(noticeId)) { return; }
    this.dismissing.add(noticeId);

    // The endpoint takes [FromBody] string? - a JSON-encoded string, not an object, and the
    // content type has to be set or HttpClient sends a bare string as text/plain.
    this.http.post<SystemNotice | null>(
      `${this.apiUrl}/DismissSystemNoticeAsync/${noticeId}`,
      JSON.stringify(feedback ?? null),
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
    )
      // An already-dismissed 400 just ends the chain rather than surfacing an error.
      .pipe(catchError(() => of(null)))
      .subscribe(next => {
        // The response is as fresh as a check, so restart the throttle from here.
        this.lastCheckedAt = Date.now();
        if (next) { this.show(next); }
      });
  }

  // #region Administration

  /** Shows an unsaved notice as users would see it; dismissing or feedback does nothing. */
  public preview(notice: SystemNotice): void {
    const previewNotice = { ...notice, Id: PREVIEW_NOTICE_ID };

    if (previewNotice.NoticeType === SystemNoticeTypeEnum.Popup) {
      this.showPopup(previewNotice);
    }
    else {
      this.showToast(previewNotice);
    }
  }

  /**
   * A page of notices for the Administration > System Notices grid. Requires the SDR role.
   * Sortable on Message, StartDateUTC, EndDateUTC and CreatedBy.
   */
  public getNotices(
    pageNumber: number,
    pageSize: number,
    sortField?: string,
    sortDirection?: 'asc' | 'desc'
  ): Observable<PaginationModel<SystemNotice>> {
    let params = new HttpParams()
      .set('PageNumber', pageNumber)
      .set('PageSize', pageSize);

    if (sortField && sortDirection) {
      params = params.set('SortField', sortField).set('SortDirection', sortDirection);
    }

    return this.http.get<PaginationModel<SystemNotice>>(`${this.apiUrl}/GetSystemNotices/`, { params })
      .pipe(map(result => ({
        ...result,
        Results: (result.Results ?? []).map(n => ({
          ...n,
          StartDateUTC: asUtc(n.StartDateUTC),
          EndDateUTC: asUtc(n.EndDateUTC),
          CreatedDateUTC: asUtc(n.CreatedDateUTC)
        }))
      })));
  }

  /**
   * Everyone who saw a notice, dismissed or not, newest first. Requires the SDR role.
   * Search narrows by user name, and the counts with it.
   */
  public getNoticeViews(
    noticeId: number,
    pageNumber: number,
    pageSize: number,
    search?: string
  ): Observable<SystemNoticeViewsPage> {
    let params = new HttpParams()
      .set('PageNumber', pageNumber)
      .set('PageSize', pageSize);

    if (search) {
      params = params.set('Search', search);
    }

    return this.http.get<SystemNoticeViewsPage>(`${this.apiUrl}/GetSystemNoticeViews/${noticeId}`, { params })
      .pipe(map(result => ({
        ...result,
        Results: (result.Results ?? []).map(v => ({
          ...v,
          ViewedDateUTC: asUtc(v.ViewedDateUTC),
          DismissedDateUTC: asUtc(v.DismissedDateUTC)
        }))
      })));
  }

  public createNotice(notice: SystemNotice): Observable<SystemNotice> {
    return this.http.post<SystemNotice>(`${this.apiUrl}/CreateSystemNotice/`, notice);
  }

  public updateNotice(notice: SystemNotice): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/UpdateSystemNotice/`, notice);
  }

  public deleteNotice(noticeId: number): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/DeleteSystemNotice/${noticeId}`, null);
  }

  /** The user is shown the notice again, as if they had never seen it. */
  public deleteNoticeView(viewId: number): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/DeleteSystemNoticeView/${viewId}`, null);
  }

  /**
   * Stores a notice image or attachment and returns its blob url. Separate from saving the
   * notice, so the admin can upload while composing one that doesn't exist yet.
   */
  public uploadFile(file: File): Observable<SystemNoticeUploadResult> {
    return from(readAsBase64(file)).pipe(
      switchMap(fileBytes => this.http.post<SystemNoticeUploadResult>(
        `${this.apiUrl}/UploadSystemNoticeFile/`,
        { FileBytes: fileBytes, ContentType: file.type, FileName: file.name }
      ))
    );
  }

  // #endregion
}

/** The server binds a byte[] from a bare base64 string - the data: prefix has to go. */
function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve((reader.result as string).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** The server sends DATETIME2 without a zone; without the Z these parse as local time. */
function asUtc(value: string | undefined): string | undefined {
  if (!value) { return value; }
  return value.endsWith('Z') ? value : value + 'Z';
}

export type SystemNoticeUploadResult = {
  BlobUrl: string,
  /** SAS-signed, for previewing the file before the notice is saved. */
  SecureUrl: string,
  FileName: string,
  Extension: string,
  ContentType: string
}

/** Only the listed user types see the notice; no rows means nobody does. */
export type SystemNoticeUserType = {
  Id?: number,
  SystemNoticeId?: number,
  UserTypeId: UserTypeEnum
}

/** No rows for a notice means every school sees it. */
export type SystemNoticeSchool = {
  Id?: number,
  SystemNoticeId?: number,
  SchoolId: number
}

export type SystemNoticeFile = {
  Id: number,
  SystemNoticeId: number,
  Title?: string,
  FileName: string,
  Extension?: string,
  ContentType?: string,
  BlobUrl: string,
  /** SAS-signed BlobUrl, populated on read. */
  SecureUrl?: string
}

export type SystemNotice = {
  Id: number,
  /** SystemNoticeTypeEnum - 0 Toast, 1 Popup. */
  NoticeType: SystemNoticeTypeEnum,
  Title: string,
  Description: string,
  MoreInfo?: boolean,
  MoreInfoTitle?: string,
  /** Rendered as HTML. */
  MoreInfoDescription?: string,
  /** Azure blob URL; SecureImageUrl is the SAS-signed version to actually render. */
  Image?: string,
  SecureImageUrl?: string,
  AllowFeedback?: boolean,
  Files?: SystemNoticeFile[],
  UserTypes?: SystemNoticeUserType[],
  Schools?: SystemNoticeSchool[],
  StartDateUTC?: string,
  EndDateUTC?: string,
  CreatedDateUTC?: string,
  CreatedBy?: number,
  CreatedByUser?: User | null,
  /** Only sent on the Administration read. */
  ViewCount?: number,
  DismissalCount?: number,
  FeedbackCount?: number
}

/** One row of dbo.SystemNoticeViews, for the Administration "who saw this" list. */
export type SystemNoticeViewRow = {
  Id: number,
  UserId: number,
  UserName?: string,
  UserTypeId: UserTypeEnum,
  ViewedDateUTC?: string,
  IsDismissed: boolean,
  /** Only set once dismissed. */
  DismissedDateUTC?: string,
  Feedback?: string
}

/** TotalCount is the view count, since every row is a view. */
export type SystemNoticeViewsPage = PaginationModel<SystemNoticeViewRow> & {
  DismissalCount: number,
  FeedbackCount: number,
  AllowFeedback: boolean
}
